import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  FilterValueList, DateRangeControl, AmountRangeControl, filterOptionsFor,
} from "@/components/expenses/FilterControls";
import { ArrowUp, ArrowDown, ListFilter, ChevronDown } from "lucide-react";

const NON_FILTERABLE = ["receipt", "assigned"];

// Column header with click-to-open sort + filter popover.
export const HeaderFilterCell = ({ c, sort, dir, onSort, filters, onFilterChange, options }) => {
  const filterable = !NON_FILTERABLE.includes(c.key);

  const hasFilter = () => {
    if (c.key === "date") return !!(filters.date_from || filters.date_to);
    if (c.key === "amount") return !!(filters.amount_min || filters.amount_max);
    return (filters[c.key] || []).length > 0;
  };
  const active = filterable && hasFilter();

  const toggleListValue = (key, value) => {
    const cur = filters[key] || [];
    onFilterChange(key, cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value]);
  };

  const clear = () => {
    if (c.key === "date") { onFilterChange("date_from", ""); onFilterChange("date_to", ""); }
    else if (c.key === "amount") { onFilterChange("amount_min", ""); onFilterChange("amount_max", ""); }
    else onFilterChange(c.key, []);
  };

  const head = (
    <span className="inline-flex items-center gap-1">
      {c.label}
      {sort === c.key && (dir === "desc" ? <ArrowDown size={11} className="text-[#7FAEF9]" /> : <ArrowUp size={11} className="text-[#7FAEF9]" />)}
      {active ? <ListFilter size={11} className="text-[#4F8EF7]" /> : filterable ? <ChevronDown size={10} className="text-[#3d3d3d]" /> : null}
    </span>
  );

  if (!filterable) {
    return <th className={`whitespace-nowrap px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-[#6b6b6b] ${c.right ? "text-right" : ""}`}>{head}</th>;
  }

  return (
    <th className={`whitespace-nowrap px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider ${c.right ? "text-right" : ""}`}>
      <Popover>
        <PopoverTrigger asChild>
          <button data-testid={`header-filter-${c.key}`}
            className={`select-none whitespace-nowrap uppercase tracking-wider transition-colors hover:text-white ${active ? "text-[#7FAEF9]" : "text-[#6b6b6b]"}`}>
            {head}
          </button>
        </PopoverTrigger>
        <PopoverContent align={c.right ? "end" : "start"} className="w-60 border-[#242424] bg-[#161616] p-2 text-white"
          data-testid={`header-filter-popover-${c.key}`}>
          {c.sortable && (
            <div className="mb-1.5 flex gap-1">
              <button onClick={() => onSort(c.key, "asc")} data-testid={`sort-asc-${c.key}`}
                className={`flex flex-1 items-center justify-center gap-1 rounded-lg py-1.5 text-[11.5px] ${sort === c.key && dir === "asc" ? "bg-[rgba(79,142,247,0.16)] text-[#7FAEF9]" : "bg-[#1c1c1c] text-[#A3A3A3] hover:text-white"}`}>
                <ArrowUp size={11} /> Sort asc
              </button>
              <button onClick={() => onSort(c.key, "desc")} data-testid={`sort-desc-${c.key}`}
                className={`flex flex-1 items-center justify-center gap-1 rounded-lg py-1.5 text-[11.5px] ${sort === c.key && dir === "desc" ? "bg-[rgba(79,142,247,0.16)] text-[#7FAEF9]" : "bg-[#1c1c1c] text-[#A3A3A3] hover:text-white"}`}>
                <ArrowDown size={11} /> Sort desc
              </button>
            </div>
          )}
          {c.key === "date" ? (
            <DateRangeControl from={filters.date_from} to={filters.date_to}
              onChange={(f, t) => { onFilterChange("date_from", f); onFilterChange("date_to", t); }} testPrefix="header" />
          ) : c.key === "amount" ? (
            <AmountRangeControl min={filters.amount_min} max={filters.amount_max}
              onApply={(lo, hi) => { onFilterChange("amount_min", lo); onFilterChange("amount_max", hi); }} testPrefix="header" />
          ) : (
            <FilterValueList options={filterOptionsFor(c.key, options)} selected={filters[c.key] || []}
              onToggle={(v) => toggleListValue(c.key, v)} testPrefix={`header-${c.key}`} />
          )}
          {active && (
            <button data-testid={`header-clear-${c.key}`} onClick={clear}
              className="mt-1.5 w-full rounded-lg bg-[#1c1c1c] py-1.5 text-[11px] text-[#A3A3A3] transition-colors hover:bg-[#242424] hover:text-white">
              Clear filter
            </button>
          )}
        </PopoverContent>
      </Popover>
    </th>
  );
};
